import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function Row({ inst, onSaved }) {
  const [e, setE] = useState({
    lot_size: inst.lot_size, strike_interval: inst.strike_interval, tick_size: inst.tick_size,
    expiry_schedule: inst.expiry_schedule, expiry_weekday: inst.expiry_weekday,
    open: inst.trading_hours?.open || "09:15", close: inst.trading_hours?.close || "15:30",
    active: inst.active,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/quantlab/instruments/${encodeURIComponent(inst.instrument_id)}`, {
        changes: {
          lot_size: Number(e.lot_size), strike_interval: Number(e.strike_interval),
          tick_size: Number(e.tick_size), expiry_schedule: e.expiry_schedule,
          expiry_weekday: Number(e.expiry_weekday),
          trading_hours: { open: e.open, close: e.close, tz: "Asia/Kolkata" },
          active: e.active,
        },
      });
      toast.success(`${inst.symbol} specs updated.`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const inp = "w-20 bg-black border border-zinc-800 px-2 py-1 text-white font-mono text-xs";
  return (
    <tr className="border-b border-zinc-900/50" data-testid={`ql-inst-${inst.symbol}`}>
      <td className="px-3 py-2 font-bold font-mono">{inst.symbol}<div className="text-[9px] text-zinc-600 font-normal">{inst.instrument_id}</div></td>
      <td className="px-3 py-2 font-mono text-zinc-400">{inst.exchange}</td>
      <td className="px-3 py-2"><input className={inp} value={e.lot_size} onChange={(v) => setE({ ...e, lot_size: v.target.value })} data-testid={`ql-inst-lot-${inst.symbol}`} /></td>
      <td className="px-3 py-2"><input className={inp} value={e.strike_interval} onChange={(v) => setE({ ...e, strike_interval: v.target.value })} /></td>
      <td className="px-3 py-2"><input className={inp} value={e.tick_size} onChange={(v) => setE({ ...e, tick_size: v.target.value })} /></td>
      <td className="px-3 py-2">
        <select className={inp + " w-24"} value={e.expiry_schedule} onChange={(v) => setE({ ...e, expiry_schedule: v.target.value })}>
          <option value="weekly">weekly</option><option value="monthly">monthly</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <select className={inp + " w-28"} value={e.expiry_weekday} onChange={(v) => setE({ ...e, expiry_weekday: v.target.value })}>
          {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
        </select>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        <input className={inp + " w-16 mr-1"} value={e.open} onChange={(v) => setE({ ...e, open: v.target.value })} />–
        <input className={inp + " w-16 ml-1"} value={e.close} onChange={(v) => setE({ ...e, close: v.target.value })} />
      </td>
      <td className="px-3 py-2">
        <input type="checkbox" checked={e.active} onChange={(v) => setE({ ...e, active: v.target.checked })} className="accent-[#22C55E]" />
      </td>
      <td className="px-3 py-2">
        <button onClick={save} disabled={busy}
                className="text-[9px] uppercase tracking-widest font-mono bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-50 text-white px-3 py-1.5"
                data-testid={`ql-inst-save-${inst.symbol}`}>{busy ? "…" : "Save"}</button>
      </td>
    </tr>
  );
}

export const QLInstruments = () => {
  const [items, setItems] = useState([]);
  const [expiries, setExpiries] = useState({});
  const load = () => api.get("/quantlab/instruments").then(async (r) => {
    setItems(r.data.items || []);
    const ex = {};
    for (const i of r.data.items || []) {
      try { ex[i.symbol] = (await api.get(`/quantlab/instruments/${i.symbol}/expiries?count=4`)).data.expiries; }
      catch { ex[i.symbol] = []; }
    }
    setExpiries(ex);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="ql-instruments">
      <div className="border border-zinc-800 bg-[#0a0a0a] overflow-x-auto">
        <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          Instrument master — engines read these values dynamically; edit here when the exchange changes specs
        </div>
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900 font-mono">
            {["Instrument", "Exch", "Lot size", "Strike int.", "Tick", "Expiry sched.", "Expiry day", "Session (IST)", "Active", ""].map((h) => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>{items.map((i) => <Row key={i.instrument_id} inst={i} onSaved={load} />)}</tbody>
        </table>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {items.map((i) => (
          <div key={i.symbol} className="border border-zinc-800 bg-[#0a0a0a] p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">{i.symbol} — next expiries</div>
            <div className="flex flex-wrap gap-2">
              {(expiries[i.symbol] || []).map((e) => (
                <span key={e} className="text-[10px] font-mono border border-zinc-800 px-2 py-1 text-zinc-300">{e}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QLInstruments;
